import React from "react";
import GoogleSearchAdChrome from "./chrome/GoogleSearchAdChrome";
import GoogleDisplayAdChrome from "./chrome/GoogleDisplayAdChrome";
import YouTubeThumbnailChrome from "./chrome/YouTubeThumbnailChrome";
import YouTubeShortChrome from "./chrome/YouTubeShortChrome";
import EmailPreviewChrome from "./chrome/EmailPreviewChrome";

/**
 * PlatformChrome
 * --------------
 * Per-platform chrome shell for the in-chat preview. Renders the platform-
 * specific top bar, action rail, and tab bar around the asset + caption,
 * matching the visual signature each platform's feed has.
 *
 * Variants supported:
 *   - Instagram (Feed / Reel / Story / Carousel)
 *   - Facebook  (Feed)
 *   - TikTok    (Feed)
 *   - LinkedIn  (Feed)
 *   - X         (Post)
 *   - YouTube   (Video / Short)         — via dedicated chrome components
 *   - Google    (Search Ad / Display)   — via dedicated chrome components
 *   - Email     (Campaign)              — via dedicated chrome component
 *
 * Children are the asset/caption/overlay subtree for the social chromes.
 * For alt chromes (YouTube / Google / Email) pass asset, caption,
 * hashtags directly on PlatformChrome — children are ignored.
 *
 * Props:
 *   - platform:  "Instagram" | "Facebook" | "TikTok" | "LinkedIn" | "YouTube"
 *                | "X" | "Google" | "Email"
 *   - postType:  "Feed" | "Story" | "Reel" | "Carousel" | "Video" | "Short"
 *                | "Post" | "Search Ad" | "Display Ad" | "Campaign"
 *                Tokens like "google_search_ad", "google_display_ad",
 *                "youtube_thumbnail", "youtube_short", "email_campaign" are
 *                also accepted on either prop and resolve to the matching
 *                chrome.
 *   - brand:     optional { handle, display_name, avatar_url, verified,
 *                            from_email, to_email, sitelinks, cta_text,
 *                            cta_url, cta_color }
 *   - asset:     optional { url, type, ... }  (for alt chromes only)
 *   - caption:   optional caption string      (for alt chromes only)
 *   - hashtags:  optional string[]            (for alt chromes only)
 *   - children:  subtree (asset, caption, overlays) for social chromes.
 */
export default function PlatformChrome({
  platform,
  postType,
  brand,
  asset,
  caption,
  hashtags,
  children,
}) {
  const p = platform || "Instagram";
  const t = postType || "Feed";
  const handle = brand?.handle || brand?.display_name || "your_brand";

  // ─── Alt-chrome routing ────────────────────────────────────────────
  // Token form: snake_case strings on either platform or postType.
  const tokens = [p, t]
    .map((s) => String(s || "").trim().toLowerCase().replace(/[\s-]+/g, "_"))
    .join("|");

  const altChrome = pickAltChrome(tokens, p, t);
  if (altChrome) {
    return altChrome({ brand, asset, caption, hashtags });
  }

  const isFullScreen =
    (p === "Instagram" && t === "Reel") ||
    (p === "TikTok" && (t === "Feed" || t === "Video")) ||
    t === "Story" ||
    t === "Short";

  if (isFullScreen) {
    // Reels / TikTok / Stories — asset fills, action rail floats on the right.
    return (
      <div
        className="vella-platform-chrome"
        data-platform={p}
        data-post-type={t}
        style={{
          position: "relative",
          width: "100%",
          height: "100%",
          background: "#000",
          color: "#fff",
          overflow: "hidden",
        }}
      >
        {children}
        <PlatformActionRail platform={p} handle={handle} />
        {p === "TikTok" ? <TikTokTopBar /> : null}
        {p === "Instagram" && t === "Reel" ? <InstagramReelTopBar /> : null}
      </div>
    );
  }

  if (p === "Facebook") {
    return (
      <div
        className="vella-platform-chrome"
        data-platform="Facebook"
        data-post-type={t}
        style={chromeBase}
      >
        <FacebookTopBar />
        <FacebookPostHeader handle={handle} />
        {children}
        <FacebookActionRow />
        <FacebookTabBar />
      </div>
    );
  }

  if (p === "LinkedIn") {
    return (
      <div
        className="vella-platform-chrome"
        data-platform="LinkedIn"
        data-post-type={t}
        style={chromeBase}
      >
        <LinkedInTopBar />
        <LinkedInPostHeader handle={handle} />
        {children}
        <LinkedInActionRow />
        <LinkedInTabBar />
      </div>
    );
  }

  if (p === "X" || p === "Twitter") {
    return (
      <div
        className="vella-platform-chrome"
        data-platform="X"
        data-post-type={t}
        style={chromeBase}
      >
        <XTopBar />
        <XPostHeader handle={handle} />
        {children}
        <XActionRow />
        <XTabBar />
      </div>
    );
  }

  // Default: Instagram Feed
  return (
    <div
      className="vella-platform-chrome"
      data-platform="Instagram"
      data-post-type={t}
      style={chromeBase}
    >
      <InstagramTopBar />
      <InstagramPostHeader handle={handle} />
      {children}
      <InstagramActionRow />
      <InstagramTabBar />
    </div>
  );
}

// ─── shared base style ──────────────────────────────────────────────
const chromeBase = {
  position: "relative",
  width: "100%",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  background: "#fff",
  color: "#0F172A",
  fontFamily:
    '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
};

const headerBase = {
  flexShrink: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "8px 14px",
  borderBottom: "1px solid #E5E7EB",
};

const tabBarBase = {
  marginTop: "auto",
  flexShrink: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-around",
  padding: "8px 12px 14px",
  borderTop: "1px solid #E5E7EB",
  background: "#fff",
};

const actionRowBase = {
  flexShrink: 0,
  display: "flex",
  alignItems: "center",
  gap: 14,
  padding: "8px 14px",
};

const dot = (size, color) => ({
  width: size,
  height: size,
  borderRadius: 9999,
  background: color,
  flexShrink: 0,
});

const Avatar = ({ size = 28 }) => (
  <div
    style={{
      width: size,
      height: size,
      borderRadius: 9999,
      background:
        "conic-gradient(from 180deg, #FCAF45, #F77737, #E1306C, #C13584, #833AB4)",
      padding: 2,
      flexShrink: 0,
    }}
  >
    <div
      style={{
        width: "100%",
        height: "100%",
        borderRadius: 9999,
        background: "#fff",
      }}
    />
  </div>
);

const TextBtn = ({ children, color = "#0F172A", weight = 500 }) => (
  <span
    style={{
      fontSize: 11,
      fontWeight: weight,
      color,
    }}
  >
    {children}
  </span>
);

// ─── Instagram Feed ─────────────────────────────────────────────────
function InstagramTopBar() {
  return (
    <div style={{ ...headerBase, paddingTop: 38 /* clear status bar */ }}>
      <span style={{ fontFamily: "cursive", fontSize: 18, fontWeight: 700 }}>
        Instagram
      </span>
      <div style={{ display: "flex", gap: 16, fontSize: 18 }}>
        <span>♡</span>
        <span>✈︎</span>
      </div>
    </div>
  );
}
function InstagramPostHeader({ handle }) {
  return (
    <div
      style={{
        ...headerBase,
        padding: "8px 12px",
        borderBottom: "none",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <Avatar size={28} />
        <span style={{ fontSize: 12, fontWeight: 600 }}>{handle}</span>
      </div>
      <span style={{ fontSize: 14 }}>⋯</span>
    </div>
  );
}
function InstagramActionRow() {
  return (
    <div style={actionRowBase}>
      <span style={{ fontSize: 22 }}>♡</span>
      <span style={{ fontSize: 22 }}>💬</span>
      <span style={{ fontSize: 22 }}>✈︎</span>
      <span style={{ marginLeft: "auto", fontSize: 22 }}>🔖</span>
    </div>
  );
}
function InstagramTabBar() {
  return (
    <div style={tabBarBase}>
      <span style={{ fontSize: 18 }}>⌂</span>
      <span style={{ fontSize: 16 }}>🔍</span>
      <span style={{ fontSize: 18 }}>＋</span>
      <span style={{ fontSize: 18 }}>♡</span>
      <div style={dot(20, "#A78BFA")} />
    </div>
  );
}

// ─── Instagram Reel — full-screen overlay rail ──────────────────────
function InstagramReelTopBar() {
  return (
    <div
      style={{
        position: "absolute",
        top: 36,
        left: 0,
        right: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "0 14px",
        zIndex: 30,
        color: "#fff",
        fontWeight: 600,
        fontSize: 13,
      }}
    >
      <span>Reels</span>
      <span style={{ fontSize: 16 }}>📷</span>
    </div>
  );
}

// ─── TikTok Feed ────────────────────────────────────────────────────
function TikTokTopBar() {
  return (
    <div
      style={{
        position: "absolute",
        top: 36,
        left: 0,
        right: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 22,
        padding: "0 14px",
        zIndex: 30,
        color: "#fff",
        fontWeight: 600,
        fontSize: 13,
      }}
    >
      <span style={{ opacity: 0.7 }}>Following</span>
      <span style={{ borderBottom: "2px solid #fff", paddingBottom: 2 }}>
        For You
      </span>
    </div>
  );
}

// ─── Shared full-screen action rail (Reels / TikTok) ────────────────
function PlatformActionRail({ platform, handle }) {
  const isTikTok = platform === "TikTok";
  return (
    <div
      style={{
        position: "absolute",
        right: 8,
        bottom: 80,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 18,
        zIndex: 25,
        color: "#fff",
        fontSize: 11,
        fontWeight: 600,
        textShadow: "0 1px 3px rgba(0,0,0,0.5)",
      }}
    >
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
        <Avatar size={36} />
        {isTikTok ? (
          <span style={{ fontSize: 14, marginTop: -4, color: "#FE2C55" }}>＋</span>
        ) : null}
      </div>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
        <span style={{ fontSize: 26 }}>♥</span>
        <span>52.4K</span>
      </div>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
        <span style={{ fontSize: 24 }}>💬</span>
        <span>1,204</span>
      </div>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
        <span style={{ fontSize: 24 }}>{isTikTok ? "↪" : "✈︎"}</span>
        <span>Share</span>
      </div>
      {!isTikTok ? (
        <span style={{ fontSize: 22 }}>⋮</span>
      ) : null}
    </div>
  );
}

// ─── Facebook ───────────────────────────────────────────────────────
function FacebookTopBar() {
  return (
    <div
      style={{
        ...headerBase,
        paddingTop: 38,
        background: "#fff",
      }}
    >
      <span style={{ fontWeight: 800, color: "#1877F2", fontSize: 22 }}>
        facebook
      </span>
      <div style={{ display: "flex", gap: 12, fontSize: 16 }}>
        <span>＋</span>
        <span>🔍</span>
        <span>💬</span>
      </div>
    </div>
  );
}
function FacebookPostHeader({ handle }) {
  return (
    <div
      style={{
        ...headerBase,
        padding: "10px 12px",
        borderBottom: "none",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <div
          style={{
            ...dot(36, "#1877F2"),
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
            fontWeight: 700,
            fontSize: 14,
          }}
        >
          {String(handle).charAt(0).toUpperCase()}
        </div>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <span style={{ fontSize: 13, fontWeight: 600 }}>{handle}</span>
          <span style={{ fontSize: 11, color: "#65676B" }}>2h · 🌐</span>
        </div>
      </div>
      <span style={{ fontSize: 14 }}>⋯</span>
    </div>
  );
}
function FacebookActionRow() {
  return (
    <div
      style={{
        ...actionRowBase,
        justifyContent: "space-around",
        borderTop: "1px solid #E5E7EB",
        gap: 0,
      }}
    >
      <TextBtn color="#65676B" weight={600}>👍 Like</TextBtn>
      <TextBtn color="#65676B" weight={600}>💬 Comment</TextBtn>
      <TextBtn color="#65676B" weight={600}>↪ Share</TextBtn>
    </div>
  );
}
function FacebookTabBar() {
  return (
    <div style={{ ...tabBarBase, color: "#1877F2" }}>
      <span style={{ fontSize: 18 }}>🏠</span>
      <span style={{ fontSize: 16, color: "#65676B" }}>👥</span>
      <span style={{ fontSize: 16, color: "#65676B" }}>🎥</span>
      <span style={{ fontSize: 16, color: "#65676B" }}>🔔</span>
      <span style={{ fontSize: 16, color: "#65676B" }}>≡</span>
    </div>
  );
}

// ─── LinkedIn ───────────────────────────────────────────────────────
function LinkedInTopBar() {
  return (
    <div
      style={{
        ...headerBase,
        paddingTop: 38,
        gap: 8,
      }}
    >
      <div
        style={{
          ...dot(28, "#0A66C2"),
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#fff",
          fontWeight: 700,
          fontSize: 14,
        }}
      >
        in
      </div>
      <div
        style={{
          flex: 1,
          background: "#EEF3F8",
          borderRadius: 6,
          padding: "6px 10px",
          fontSize: 12,
          color: "#65676B",
        }}
      >
        Search
      </div>
      <span style={{ fontSize: 16, color: "#65676B" }}>💬</span>
    </div>
  );
}
function LinkedInPostHeader({ handle }) {
  return (
    <div
      style={{
        ...headerBase,
        padding: "10px 12px",
        borderBottom: "none",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <Avatar size={36} />
        <div style={{ display: "flex", flexDirection: "column" }}>
          <span style={{ fontSize: 13, fontWeight: 700 }}>{handle}</span>
          <span style={{ fontSize: 10, color: "#65676B" }}>
            Founder · 1st
          </span>
          <span style={{ fontSize: 10, color: "#65676B" }}>2h · 🌐</span>
        </div>
      </div>
      <span style={{ fontSize: 14 }}>⋯</span>
    </div>
  );
}
function LinkedInActionRow() {
  return (
    <div
      style={{
        ...actionRowBase,
        justifyContent: "space-around",
        borderTop: "1px solid #E5E7EB",
        gap: 0,
      }}
    >
      <TextBtn color="#65676B" weight={600}>👍 Like</TextBtn>
      <TextBtn color="#65676B" weight={600}>💬 Comment</TextBtn>
      <TextBtn color="#65676B" weight={600}>↻ Repost</TextBtn>
      <TextBtn color="#65676B" weight={600}>↗ Send</TextBtn>
    </div>
  );
}
function LinkedInTabBar() {
  return (
    <div style={tabBarBase}>
      <span style={{ fontSize: 18, color: "#0A66C2" }}>🏠</span>
      <span style={{ fontSize: 16, color: "#65676B" }}>👥</span>
      <span style={{ fontSize: 16, color: "#65676B" }}>＋</span>
      <span style={{ fontSize: 16, color: "#65676B" }}>🔔</span>
      <span style={{ fontSize: 16, color: "#65676B" }}>💼</span>
    </div>
  );
}

// ─── X (Twitter) ────────────────────────────────────────────────────
function XTopBar() {
  return (
    <div
      style={{
        ...headerBase,
        paddingTop: 38,
        justifyContent: "center",
      }}
    >
      <span style={{ fontSize: 22, fontWeight: 800 }}>𝕏</span>
    </div>
  );
}
function XPostHeader({ handle }) {
  return (
    <div
      style={{
        ...headerBase,
        padding: "10px 12px",
        borderBottom: "none",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <Avatar size={36} />
        <div style={{ display: "flex", flexDirection: "column" }}>
          <span style={{ fontSize: 13, fontWeight: 700 }}>{handle}</span>
          <span style={{ fontSize: 11, color: "#536471" }}>
            @{handle} · 2h
          </span>
        </div>
      </div>
      <span style={{ fontSize: 14 }}>⋯</span>
    </div>
  );
}
function XActionRow() {
  return (
    <div
      style={{
        ...actionRowBase,
        justifyContent: "space-between",
        color: "#536471",
      }}
    >
      <TextBtn color="#536471">💬 24</TextBtn>
      <TextBtn color="#536471">↻ 87</TextBtn>
      <TextBtn color="#536471">♡ 412</TextBtn>
      <TextBtn color="#536471">📊 1.2K</TextBtn>
      <TextBtn color="#536471">↗</TextBtn>
    </div>
  );
}
function XTabBar() {
  return (
    <div style={tabBarBase}>
      <span style={{ fontSize: 18 }}>🏠</span>
      <span style={{ fontSize: 16, color: "#536471" }}>🔍</span>
      <span style={{ fontSize: 16, color: "#536471" }}>🔔</span>
      <span style={{ fontSize: 16, color: "#536471" }}>✉</span>
    </div>
  );
}

// ─── Alt-chrome routing helpers ─────────────────────────────────────
// Maps the tokens documented on PlatformChrome to a renderer function
// (props) => ReactNode. Renderer pulls asset/caption/hashtags off the
// PlatformChrome props and forwards brand fields to the dedicated chrome.
//
// Token combinations matched:
//   google_search_ad  → GoogleSearchAdChrome
//   google_display_ad → GoogleDisplayAdChrome
//   youtube_thumbnail → YouTubeThumbnailChrome (also Youtube + Video)
//   youtube_short     → YouTubeShortChrome     (also Youtube + Short)
//   email_campaign    → EmailPreviewChrome     (also Email + anything)
function pickAltChrome(tokens, platform, postType) {
  const p = String(platform || "").toLowerCase();
  const t = String(postType || "").toLowerCase();
  const has = (s) => tokens.includes(s);

  if (has("google_search_ad")) return renderGoogleSearchAd;
  if (has("google_display_ad")) return renderGoogleDisplayAd;
  if (has("youtube_thumbnail")) return renderYouTubeThumbnail;
  if (has("youtube_short")) return renderYouTubeShort;
  if (has("email_campaign")) return renderEmailCampaign;

  if (p === "google" || p === "google_ads") {
    if (t.includes("display")) return renderGoogleDisplayAd;
    return renderGoogleSearchAd;
  }
  if (p === "youtube") {
    if (t === "short" || t === "shorts") return renderYouTubeShort;
    if (t === "video" || t === "thumbnail" || t === "feed" || t === "post") {
      return renderYouTubeThumbnail;
    }
  }
  if (p === "email" || p === "mailchimp" || p === "klaviyo") {
    return renderEmailCampaign;
  }
  return null;
}

function assetUrlOf(asset) {
  if (!asset) return undefined;
  if (typeof asset === "string") return asset;
  return asset.url || asset.asset_url || undefined;
}

function renderGoogleSearchAd({ brand, asset, caption }) {
  return (
    <GoogleSearchAdChrome
      headline={brand?.headline || caption || undefined}
      headline2={brand?.headline2}
      headline3={brand?.headline3}
      displayUrl={brand?.display_url || brand?.url}
      description={brand?.description || caption || undefined}
      description2={brand?.description2}
      sitelinks={brand?.sitelinks || []}
      brandName={brand?.display_name || brand?.handle || "Your Brand"}
    />
  );
}

function renderGoogleDisplayAd({ brand, asset, caption }) {
  return (
    <GoogleDisplayAdChrome
      assetUrl={assetUrlOf(asset)}
      headline={brand?.headline || caption || undefined}
      description={brand?.description}
      ctaText={brand?.cta_text || "Shop now"}
      advertiser={brand?.display_url || brand?.url || brand?.handle || "yourbrand.com"}
      ctaColor={brand?.cta_color}
    />
  );
}

function renderYouTubeThumbnail({ brand, asset, caption }) {
  return (
    <YouTubeThumbnailChrome
      assetUrl={assetUrlOf(asset)}
      title={caption || brand?.title}
      channelName={brand?.display_name || brand?.handle || "Your Brand"}
      channelAvatarUrl={brand?.avatar_url}
      viewCount={brand?.view_count}
      uploadedAgo={brand?.uploaded_ago}
      duration={brand?.duration}
      verified={!!brand?.verified}
    />
  );
}

function renderYouTubeShort({ brand, asset, caption, hashtags }) {
  return (
    <YouTubeShortChrome
      assetUrl={assetUrlOf(asset)}
      title={caption || brand?.title}
      hashtags={hashtags || []}
      channelName={brand?.handle || brand?.display_name || "your_brand"}
      channelAvatarUrl={brand?.avatar_url}
      likeCount={brand?.like_count}
      commentCount={brand?.comment_count}
      musicTitle={brand?.music_title}
    />
  );
}

function renderEmailCampaign({ brand, asset, caption }) {
  return (
    <EmailPreviewChrome
      assetUrl={assetUrlOf(asset)}
      subject={brand?.subject || caption || undefined}
      fromName={brand?.display_name || brand?.from_name || "Your Brand"}
      fromEmail={brand?.from_email}
      toEmail={brand?.to_email}
      preheader={brand?.preheader}
      body={brand?.body}
      ctaText={brand?.cta_text}
      ctaUrl={brand?.cta_url}
      ctaColor={brand?.cta_color}
    />
  );
}
